// Meal Model
import { Model, Repository, useRepo } from 'pinia-orm'
import { NumberCast, BooleanCast } from 'pinia-orm/casts'
import { Composition, CompositionRepo } from '@/models/Composition'
import { Recipe, RecipeRepo } from '@/models/Recipe'
import { RecipeMeal, RecipeMealRepo } from '@/models/RecipeMeal'
import { Globalconfig } from '@/models/Globalconfig'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import axios from 'axios'
import _ from 'lodash'

class Meal extends Model {

  // This is the name used as module name of the Vuex Store.
  static entity = 'meals'

  // List of all fields (schema) of the post model. `this.attr` is used
  // for the generic field type. The argument is the default value.
  static fields() {
    return {
      id: this.attr(0).notNullable(),
      name: this.string(''),
      date: this.string(''),
      type: this.string(''), // type of meal amongst breakfast, lunch, diner
      nb_people: this.attr(0),
      ignored: this.attr(false),
      comment: this.string(''),
      ignored_type: this.string(''),
      leftover_source_ids: this.attr(null),
      composition: this.hasMany(Composition, 'meal_id'),
      recipes: this.hasMany(RecipeMeal, 'meal_id'),
    }
  }

  static casts() {
    return {
      id: NumberCast,
      nb_people: NumberCast,
      ignored: BooleanCast
    }
  }

  static mutators() {
    return {
      leftover_source_ids(value) {
        if (typeof value === 'string') {
          try {
            return JSON.parse(value)
          }
          catch (e) {
            return []
          }
        }
        else {
          if (Array.isArray(value)) {
            return value
          }
          else {
            return []
          }
        }
      }
    }
  }

  static defaults() {
    let nb_people = 1
    const cc = useRepo(Globalconfig).find("currentCommunity")
    if (cc) {
      nb_people = cc.value.default_nb_people
    }
        return {
      id: 0,
      name: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'lunch', // type of meal amongst breakfast, lunch, diner
      nb_people: nb_people,
      ignored: false,
      comment: '',
      composition: [],
      recipes: []
    }
  }

  static allTypes() {
    return [
      { name: 'lunch', label: 'Midi' },
      { name: 'lunchalt', label: 'Midi alternatif' },
      { name: 'diner', label: 'Soir' },
      { name: 'dineralt', label: 'Soir alternatif' }
    ]
  }

  get isAlt() {
    return ['lunchalt', 'dineralt'].indexOf(this.type) > -1
  }

    get shortDate() {
    return this.date ? format(new Date(this.date), 'eee dd/MM', { locale: fr }) : ''
  }

  get summary () {
    const formattedDate = this.date ? format(new Date(this.date), 'dd/MM/yy') : ''
    return '(' + formattedDate + ') ' + _.upperFirst(this.name) + ' - ' + this.nb_people + 'p.'
  }

  get bakingTypes() {
    if (this && this.recipes && this.recipes.length) {
      const recipesId = this.recipes.reduce((prev, curr) => prev.concat([curr.recipe_id]), [])
      const recipes = useRepo(Recipe).find(recipesId)
      if (recipes && recipes.length) {
        const bt = Recipe.bakingTypes()
        const types = []
        recipes.map((r) => {
          if (r.baking_type) {
            types.push(bt.filter((v) => v.value === r.baking_type)[0].text)
          }
        })
        if (types.length) {
          return types.join(', ')
        }

        return ''
      }

      return ''
    }

    return ''
  }
}

class MealRepo extends Repository {
  use = Meal

  async $fetch(firstDay, nbDays) {
    let data = {}
    if(firstDay) {
      data.firstDay = firstDay
    }
    if (nbDays) {
      data.nbDays = nbDays
    }
    return new Promise(async (resolve, reject) => {
      try {
        const meals = await axios.get('/meals/get.php', {
          params: data
        })
        resolve(this.save(meals.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $fetchForGrocery(listId) {
    let uri = '/meals/get.php?filter=hasGrocery'
    if (listId) {
      uri += '&listId=' + encodeURIComponent(listId)
    }
    return new Promise(async (resolve, reject) => {
      try {
        const meals = await axios.get(uri)
        resolve(this.save(meals.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $get(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const meal = await axios.get('/meals/get.php?id=' + id)
        resolve(this.save(meal.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $getBulk(ids) {
    return new Promise(async (resolve, reject) => {
      try {
        const meal = await axios.get('/meals/get.php?filter=ids&ids=' + encodeURIComponent(JSON.stringify(ids)))
        resolve(this.save(meal.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $getByDayOfWeek(day, type = '') {
    return new Promise(async (resolve, reject) => {
      try {
        const meals = await axios.get('/meals/get.php?filter=byDayOfWeek&dayOfWeek=' + encodeURIComponent(day) + '&mealType=' + encodeURIComponent(type))
        meals.data = meals.data.filter((value, index, array) => array.findIndex((item) => item.name.toUpperCase() === value.name.toUpperCase()) === index)
        resolve(meals)
      }
      catch (e) {
        reject()
      }
    })
  }

  async $create(data) {
    let filteredData = data
    if (data.recipes) {
      data.recipes.map(i => {
        delete i.recipe
      })
    }
    return new Promise(async (resolve, reject) => {
      try {
        const meal = await axios.post('/meals/post.php', filteredData)
        resolve(this.save(meal.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $update(id, data, resetRecipes = true) {
    let filteredData = data
    if (resetRecipes) {
      if (data.recipes) {
        data.recipes.map(i => {
          delete i.recipe
        })
      }
      // Delete linked between recipe and ingredients since they will be re-created
      useRepo(RecipeMeal).where('meal_id', id).delete()
    }
    else if (data.recipes) {
      data.recipes.map(i => {
        delete i.recipe
      })
    }
    return new Promise(async (resolve, reject) => {
      try {
        const meal = await axios.put('/meals/put.php?id=' + id, filteredData)
        resolve(this.save(meal.data))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $delete(id) {
    return new Promise(async (resolve, reject) => {
      try {
        await axios.delete('/meals/delete.php?id=' + id)
        resolve(this.destroy(id))
      }
      catch (e) {
        reject()
      }
    })
  }

  async $bulkDelete(ids) {
    return new Promise(async (resolve, reject) => {
      try {
        await axios.post('/meals/bulkDelete/post.php', { ids: ids })
        resolve(this.where(m => ids.indexOf(m.id)>-1).delete())
      }
      catch (e) {
        reject()
      }
    })
  }

  async $loadRecipes(id) {
    return new Promise(async function (resolve, reject) {
      try {
        await useRepo(RecipeRepo).$getByMeal(id)
        await useRepo(RecipeMealRepo).$getByMeal(id)
        resolve()
      }
      catch (e) {
        reject()
      }
    })
  }

  async $updateIngredients(id) {
    await axios.post('/meals/ingredients/post.php?id=' + id, {})
    // Deleting previous composition for this meal
    useRepo(Composition).where('meal_id', id).delete()
    // Getting all new compositions
    await useRepo(CompositionRepo).$fetch([{ id: id }])
  }

    async $getWeekScore(firstDay) {
    let data = {}
    if (firstDay) {
      data.firstDay = firstDay
    }
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get('/meals/weekScore/get.php', {
          params: data
        })
        if (response.data && typeof response.data === 'object') {
          resolve(response.data)
        } else {
          resolve({ score: response.data, tips: [] })
        }
      }
      catch (e) {
        reject(e)
      }
    })
  }
}

export {
  Meal,
  MealRepo
}